import { ExpandedRows } from '../types/expandedRows'
import { BaseInputGroupRow, BaseInputRowComponent, NestedChildrenTypes, PeriodValue } from '../types/inputTypes'
import { ReportRowType } from '../types/reportRowType'

export type BalanceMapper<TInputBalance, TTableBalance> = (
  columnValues: TInputBalance,
  periods?: PeriodValue[],
) => TTableBalance[]

export interface GenericTableRow<TBalance> {
  type: ReportRowType
  id: string
  originalId?: string
  label: string
  sublabel?: string
  balances: TBalance[]
  hasChildren?: boolean
}

export const buildTableData = <TInputBalance, TTableBalance>(
  components: BaseInputRowComponent<TInputBalance>[],
  expanded: ExpandedRows,
  periods: PeriodValue[],
  balanceMapper: BalanceMapper<TInputBalance, TTableBalance>,
): GenericTableRow<TTableBalance>[] => {
  const rows: GenericTableRow<TTableBalance>[] = []

  const pushAccountsGroupRows = (group: BaseInputGroupRow<TInputBalance>) => {
    const { label, children, column_values: columnValues, uniqueId } = group

    rows.push({
      type: ReportRowType.Group,
      id: uniqueId || label,
      label,
      balances: balanceMapper(columnValues, periods),
      hasChildren: children && Object.keys(children).length > 0,
    })

    if (expanded[uniqueId || label] && children && Object.keys(children).length > 0) {
      for (const accountId of Object.keys(children)) {
        const account = children[accountId]
        const { id, label: accountLabel, accountNumber, column_values: accountColumnValues, uniqueId } = account

        if (id && accountLabel) {
          const label = `${accountNumber} - ${accountLabel}`

          rows.push({
            type: ReportRowType.Account,
            id: uniqueId || id,
            originalId: id,
            label,
            balances: balanceMapper(accountColumnValues, periods),
          })
        }
      }
    }
  }

  const processSectionChildren = (children: NestedChildrenTypes<TInputBalance>[]) => {
    for (let i = 0; i < children.length; i++) {
      const child = children[i]

      if (child.type === ReportRowType.Sum) {
        const { label, column_values: columnValues, uniqueId, sublabel } = child
        const type = i === 0 ? ReportRowType.Header : ReportRowType.Footer

        rows.push({
          type,
          id: uniqueId || `${type}-${label.replaceAll(' ', '-')}`,
          label,
          sublabel,
          balances: balanceMapper(columnValues, periods),
        })
      } else if (child.type === ReportRowType.Group) {
        pushAccountsGroupRows(child)
      } else if (child.type === ReportRowType.Section) {
        if (child.children && child.children.length > 0) {
          processSectionChildren(child.children)
        }
      }
    }
  }

  for (const section of components) {
    if (section.type === ReportRowType.Section) {
      const { children } = section

      if (children && children.length > 0) {
        processSectionChildren(children)
      }
    }
  }

  return rows
}
